import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { enableDeviceMode, listDevices, revokeDeviceMode, type PublicDeviceSession } from './api';
import { useSession } from './session';
import { formatRelativeDate } from '@mlc/shared';
import { Banner, Button, TextInput } from './ui';

/**
 * Mode cuisine partagée — réglages (US-50, vague E).
 *
 * « Trois actions maximum entre l'ouverture de l'application et la première
 * étape d'une recette connue » (§4) et le cloisonnement (§1) se contredisent
 * frontalement sur la tablette du plan de travail. Ce mode les réconcilie :
 * l'appareil ouvre **le livre commun**, en lecture, sans mot de passe et sans
 * rien qui appartienne à quelqu'un.
 *
 * **Il s'active depuis l'appareil qu'on veut passer en mode partagé**, et
 * nulle part ailleurs : le jeton est posé en cookie et n'est jamais renvoyé,
 * donc il n'y a rien à copier, rien à transmettre, rien qui puisse traîner
 * dans un message.
 */
export function DeviceMode(): React.JSX.Element {
  const [appareils, setAppareils] = useState<PublicDeviceSession[]>([]);
  const [label, setLabel] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Instant figé au montage : un rendu doit être pur, et « vu il y a 3 jours »
  // n'a pas besoin d'être recalculé à la seconde (même règle que RecipeList).
  const [maintenant] = useState(() => Date.now());

  const recharger = useCallback(() => {
    listDevices()
      .then(setAppareils)
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }, []);

  useEffect(recharger, [recharger]);

  function activer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    enableDeviceMode(label)
      .then(() => {
        setLabel('');
        setNotice('Cet appareil est passé en mode cuisine partagée. Déconnectez-vous pour le laisser en cuisine.');
        recharger();
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }

  return (
    <div className="flex flex-col gap-3">
      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}
      {notice !== null && <Banner tone="success">{notice}</Banner>}

      <p className="text-sm text-foreground-secondary">
        Un appareil en mode partagé ouvre les recettes de la famille sans mot de passe, en lecture seule. Il ne
        peut rien modifier, ne tient aucun journal, et ne voit aucune recette privée — pas même la vôtre.
      </p>

      {appareils.length > 0 && (
        <ul className="flex flex-col gap-2">
          {appareils.map((appareil) => (
            <li
              key={appareil.id}
              className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-surface-raised px-3 py-2"
            >
              <span>{appareil.label}</span>
              <span className="text-sm text-foreground-secondary">
                {appareil.last_seen_at === null
                  ? 'jamais utilisé'
                  : `vu ${formatRelativeDate(appareil.last_seen_at, maintenant)}`}
                {appareil.created_by_name !== null && <> · activé par {appareil.created_by_name}</>}
              </span>
              <Button
                variant="ghost"
                className="ml-auto"
                onClick={() => {
                  revokeDeviceMode(appareil.id)
                    .then(() => { setNotice(`${appareil.label} n’a plus accès au livre.`); recharger(); })
                    .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
                }}
              >
                Révoquer
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={activer} className="flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Passer <strong>cet appareil</strong> en mode partagé
          <TextInput
            type="text"
            placeholder="Tablette de la cuisine"
            value={label}
            onChange={(e) => { setLabel(e.target.value); }}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={label.trim() === ''}>Activer ici</Button>
      </form>
    </div>
  );
}

/**
 * Bandeau permanent du mode partagé.
 *
 * Discret, mais toujours là : **un appareil dont on ignore le mode est un
 * appareil sur lequel on se croira identifié**, et on s'étonnera de ne pas
 * retrouver ses recettes ou de ne pas pouvoir noter une réalisation.
 */
export function DeviceModeBanner(): React.JSX.Element | null {
  // Lu depuis l'état de session, déjà chargé une fois et mémorisé : une route
  // dédiée aurait fait payer un aller-retour de plus, à chaque écran et à tout
  // le monde, pour une information qui ne change jamais.
  const session = useSession();
  if (session?.device == null) return null;

  return (
    <p
      role="status"
      className="rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-foreground-secondary"
    >
      Mode cuisine partagée · {session.device.label} — les recettes de la famille, en lecture seule.
    </p>
  );
}
